import { useEffect, useState, useRef } from "react";
import api, { fmtDT, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Loading, EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { Search, Plus, X, PackageSearch, MessageSquarePlus, MapPin, CheckCircle2, Lock, Store, Building2 } from "lucide-react";

function ProductAutocomplete({ value, onChange, onPick }) {
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const handle = (v) => {
    onChange(v);
    clearTimeout(timer.current);
    if (v.length < 2) { setSugg([]); return; }
    timer.current = setTimeout(() => {
      api.get("/products/search", { params: { q: v } }).then((r) => { setSugg(r.data); setOpen(true); }).catch(() => {});
    }, 250);
  };

  return (
    <div className="relative">
      <input data-testid="pr-product-input" value={value} onChange={(e) => handle(e.target.value)}
        placeholder="Nom du produit (ex. Eucerin sérum...)" autoComplete="off"
        className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm" />
      {open && sugg.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {sugg.map((s) => (
            <button key={s.id} type="button" onClick={() => { onPick(s); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors duration-150 text-sm">
              <div className="font-semibold text-slate-800">{s.name}</div>
              <div className="text-xs text-slate-400">{s.supplier_name} · {s.category}{s.public_price ? ` · ${fmtDT(s.public_price)}` : ""}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductRequests() {
  const { user } = useAuth();
  const isPharmacist = user?.role === "pharmacist";
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_name: "", code: "", quantity: "", note: "" });
  const [respFor, setRespFor] = useState(null);
  const [resp, setResp] = useState({ message: "", price: "", available: true });

  const load = () => {
    const params = tab === "mine" ? null : (tab === "all" ? {} : { status: "open" });
    const url = tab === "mine" ? "/product-requests/mine" : "/product-requests";
    api.get(url, { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const submit = async () => {
    if (!form.product_name) { toast.error("Indiquez le produit recherché"); return; }
    try {
      await api.post("/product-requests", { ...form, quantity: form.quantity ? parseInt(form.quantity, 10) : null });
      toast.success("Recherche publiée au réseau");
      setOpen(false); setForm({ product_name: "", code: "", quantity: "", note: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendResponse = async () => {
    if (!resp.message) { toast.error("Écrivez un message"); return; }
    try {
      await api.post(`/product-requests/${respFor}/respond`, { message: resp.message, price: resp.price ? parseFloat(resp.price) : null, available: resp.available });
      toast.success("Réponse envoyée");
      setRespFor(null); setResp({ message: "", price: "", available: true }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const close = async (id) => {
    try { await api.post(`/product-requests/${id}/close`); toast.success("Demande clôturée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";
  const tabs = isPharmacist ? [["open", "Ouvertes"], ["mine", "Mes recherches"], ["all", "Toutes"]] : [["open", "Ouvertes"], ["all", "Toutes"]];

  return (
    <div>
      <PageHeader title="Recherche de produits" subtitle="Trouvez un produit en rupture auprès du réseau d'adhérents et fournisseurs.">
        {isPharmacist && (
          <button onClick={() => setOpen(true)} data-testid="new-request" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Nouvelle recherche</button>
        )}
      </PageHeader>

      <div className="flex gap-2 mb-5">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`pr-tab-${v}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${tab === v ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState title="Aucune recherche" subtitle={isPharmacist ? "Publiez une recherche pour solliciter le réseau." : "Aucune demande de produit pour le moment."} icon={PackageSearch} />
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`request-${r.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex gap-3">
                  <span className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center shrink-0"><PackageSearch className="h-5 w-5" /></span>
                  <div>
                    <div className="font-bold text-slate-900">{r.product_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{r.pharmacist_name}</span>
                      {r.pharmacist_city && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{r.pharmacist_city}</span>}
                      <span>· {fmtDate(r.created_at)}</span>
                      {r.quantity ? <span className="num">· Qté {r.quantity}</span> : null}
                    </div>
                    {r.note && <p className="mt-2 text-sm text-slate-600">{r.note}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status === "open" ? "active" : "closed"} />
                  {r.status === "open" && !(isPharmacist && r.pharmacist_id === user.id) && (
                    <button onClick={() => setRespFor(r.id)} data-testid={`respond-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors duration-200"><MessageSquarePlus className="h-3.5 w-3.5" /> Répondre</button>
                  )}
                  {r.status === "open" && isPharmacist && r.pharmacist_id === user.id && (
                    <button onClick={() => close(r.id)} data-testid={`close-request-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-semibold transition-colors duration-200"><Lock className="h-3.5 w-3.5" /> Clôturer</button>
                  )}
                </div>
              </div>

              {r.responses?.length > 0 && (
                <div className="mt-4 pl-4 border-l-2 border-slate-100 space-y-3">
                  {r.responses.map((resp, i) => (
                    <div key={i} className="flex items-start gap-2.5" data-testid={`response-${r.id}-${i}`}>
                      <span className={`h-7 w-7 rounded-md grid place-items-center shrink-0 ${resp.responder_role === "supplier" ? "bg-sky-50 text-sky-600" : "bg-emerald-50 text-emerald-600"}`}>
                        {resp.responder_role === "supplier" ? <Building2 className="h-4 w-4" /> : <Store className="h-4 w-4" />}
                      </span>
                      <div className="flex-1">
                        <div className="text-sm"><span className="font-semibold text-slate-800">{resp.responder_name}</span> <span className="text-xs text-slate-400">· {resp.responder_role === "supplier" ? "Fournisseur" : "Pharmacien"}</span></div>
                        <div className="text-sm text-slate-600">{resp.message}</div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs">
                          {resp.available && <span className="text-emerald-600 font-semibold flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Disponible</span>}
                          {resp.price ? <span className="num font-semibold text-slate-700">{fmtDT(resp.price)}</span> : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New request modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="request-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Je recherche un produit</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Produit recherché</label>
                <ProductAutocomplete value={form.product_name} onChange={(v) => setForm({ ...form, product_name: v })} onPick={(s) => setForm({ ...form, product_name: s.name, code: s.code || "" })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Code (optionnel)</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={input} /></div>
                <div><label className="text-sm font-semibold text-slate-700">Quantité</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={input} /></div>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Note</label><textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input + " h-20 py-2"} placeholder="Précisez le contexte (rupture, urgence...)" /></div>
            </div>
            <button onClick={submit} data-testid="submit-request" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Publier au réseau</button>
          </div>
        </div>
      )}

      {/* Respond modal */}
      {respFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setRespFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="respond-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Répondre à la recherche</h3><button onClick={() => setRespFor(null)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Message</label><textarea data-testid="respond-message" value={resp.message} onChange={(e) => setResp({ ...resp, message: e.target.value })} className={input + " h-20 py-2"} placeholder="J'ai ce produit en stock..." /></div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div><label className="text-sm font-semibold text-slate-700">Prix proposé (DT)</label><input type="number" value={resp.price} onChange={(e) => setResp({ ...resp, price: e.target.value })} className={input} /></div>
                <label className="flex items-center gap-2 h-10 text-sm font-medium text-slate-700"><input type="checkbox" checked={resp.available} onChange={(e) => setResp({ ...resp, available: e.target.checked })} className="h-4 w-4 accent-indigo-600" /> Disponible</label>
              </div>
            </div>
            <button onClick={sendResponse} data-testid="submit-response" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Envoyer la réponse</button>
          </div>
        </div>
      )}
    </div>
  );
}
